


import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { STORAGE_KEYS } from "../../utils/constants";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  CurrencyRupeeIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  XMarkIcon,
  ListBulletIcon,
  PhoneIcon,
  UserIcon,
  TruckIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";

const ServiceHistory = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [serviceHistory, setServiceHistory] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [filter, setFilter]                 = useState("all");
  const [searchTerm, setSearchTerm]         = useState("");
  const [selectedService, setSelectedService]   = useState(null);
  const [showServicesModal, setShowServicesModal] = useState(false);
  const [selectedDetails, setSelectedDetails]   = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_BASE_URL;

  // ── helpers ────────────────────────────────────────────────────
  const getCurrentUser = () => {
    if (user) return user;
    try {
      const cached = localStorage.getItem(STORAGE_KEYS.USER);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const currentUser = getCurrentUser();

  const normalizeStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    if (s.includes("pending"))   return "pending";
    if (s.includes("accepted"))  return "accepted";
    if (s.includes("rejected"))  return "rejected";
    if (s.includes("assigned"))  return "assigned";
    if (s.includes("pick"))      return "vehicle pick";
    if (s.includes("process"))   return "in progress";
    if (s.includes("done"))      return "servicing done";
    if (s.includes("drop"))      return "vehicle drop";
    if (s.includes("complete"))  return "completed";
    return "pending";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
      });
    } catch {
      return String(dateStr);
    }
  };

  const buildLocationText = (b) => {
    if (Number(b.pickup_drop) !== 1) return "Service Center Visit";
    const parts = [
      b.doorstep_flat_no,
      b.doorstep_area,
      b.doorstep_landmark,
      b.doorstep_city,
      b.doorstep_pincode || b.pincode,
    ].filter(Boolean);
    return parts.length ? parts.join(", ") : "Doorstep Service";
  };

  // ── map API response → UI model ────────────────────────────────
  const mapBooking = (b) => ({
    id:           b.booking_id,
    bookingId:    b.booking_id,
    orderId:      `BK${String(b.booking_id).padStart(4, "0")}`,

    // user info — comes from JOIN or fallback to logged-in user
    userName:   b.user?.name  || currentUser?.name  || "User",
    userPhone:  b.user?.phone || currentUser?.phone || currentUser?.phoneNumber || "N/A",
    userEmail:  b.user?.email || currentUser?.email || null,

    // vehicle
    vehicleNumber: b.vehicle_number || "N/A",
    vehicleBrand:  b.bike_brand     || "N/A",
    vehicleModel:  b.bike_model     || "N/A",
    fuelType:      b.fuel_type      || "N/A",
    kilometer:     b.kilometer      || null,

    // service
    serviceType:       b.service_type      || "Vehicle Service",
    issueDescription:  b.issue_description || "No additional comments",
    location:          buildLocationText(b),

    // schedule
    date: formatDate(b.service_date || b.created_at),
    time: b.service_time_slot || "N/A",
    createdAt: formatDate(b.created_at),

    // payment
    amount:        Number(b.total_amount    || 0),
    paymentStatus: b.payment_status         || "Pending",
    paymentMethod: (b.payment_type || "Cash").toUpperCase(),

    // status
    bookingStatus: b.booking_status || "Pending",
    status:        normalizeStatus(b.booking_status),

    // services array — b.services from BookingService JOIN
    services: Array.isArray(b.services)
      ? b.services.map((s) => ({
          serviceId:    s.service_id,
          serviceName:  s.service_name || `Service #${s.service_id}`,
          servicePrice: Number(s.price    || 0),
          quantity:     Number(s.quantity || 1),
        }))
      : [],

    // assigned store — from assignedStore JOIN
    store: b.assignedStore
      ? {
          id:      b.assignedStore.store_id,
          name:    b.assignedStore.name    || "Assigned Store",
          contact: b.assignedStore.contact || "N/A",
          address: b.assignedStore.address || "N/A",
          gst_number: b.assignedStore.gst_number || null,
        }
      : null,
      centerLocation: Number(b.pickup_drop) !== 1 ? b.center_address || "Visit nearest service center" : null,
  });

  // ── fetch ──────────────────────────────────────────────────────
  const fetchHistory = async () => {
    if (!isAuthenticated) return;

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (!token) {
      setError("Missing authentication token. Please login again.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Uses JWT — no userId param needed
      const { data } = await axios.get(`${API_BASE_URL}/booking/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const bookings = data?.data || [];
      setServiceHistory(bookings.map(mapBooking));
    } catch (err) {
      console.error("ServiceHistory fetch error:", err);
      if (err.response?.status === 401) {
        setError("Session expired. Please login again.");
      } else {
        setError("Failed to load service history. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, [isAuthenticated]);

  // ── status config ──────────────────────────────────────────────
  const getStatusConfig = (status) => {
    switch ((status || "").toLowerCase()) {
      case "completed":
        return { color: "text-green-700 bg-green-50 border-green-200",  icon: <CheckCircleIcon className="h-4 w-4" />, label: "Completed"  };
      case "pending":
        return { color: "text-yellow-700 bg-yellow-50 border-yellow-200", icon: <ClockIcon className="h-4 w-4" />, label: "Pending" };
      case "accepted":
        return { color: "text-blue-700 bg-blue-50 border-blue-200",    icon: <CheckCircleIcon className="h-4 w-4" />, label: "Accepted"  };
      case "in progress":
      case "servicing done":
      case "vehicle pick":
      case "vehicle drop":
      case "assigned":
        return { color: "text-indigo-700 bg-indigo-50 border-indigo-200", icon: <WrenchScrewdriverIcon className="h-4 w-4" />, label: status };
      case "rejected":
        return { color: "text-red-700 bg-red-50 border-red-200",       icon: <XCircleIcon className="h-4 w-4" />, label: "Rejected" };
      default:
        return { color: "text-gray-700 bg-gray-50 border-gray-200",    icon: <ExclamationTriangleIcon className="h-4 w-4" />, label: status };
    }
  };

  // ── progress steps for timeline ────────────────────────────────
  const BOOKING_STEPS = [
    { key: "pending",      label: "Booking Placed" },
    { key: "accepted",     label: "Store Accepted" },
    { key: "vehicle pick", label: "Vehicle Pickup" },
    { key: "in progress",  label: "Servicing" },
    { key: "servicing done", label: "Service Done" },
    { key: "vehicle drop", label: "Vehicle Drop" },
    { key: "completed",    label: "Completed" },
  ];

  const getCurrentStep = (status) =>
    BOOKING_STEPS.findIndex((s) => s.key === status);

  // ── filters ────────────────────────────────────────────────────
  const filteredHistory = useMemo(() => {
    return serviceHistory.filter((s) => {
      const matchFilter = filter === "all" || s.status === filter;
      const term = searchTerm.toLowerCase();
      const matchSearch =
        (s.vehicleNumber || "").toLowerCase().includes(term) ||
        (s.serviceType   || "").toLowerCase().includes(term) ||
        (s.orderId       || "").toLowerCase().includes(term) ||
        String(s.bookingId || "").includes(term);
      return matchFilter && matchSearch;
    });
  }, [serviceHistory, filter, searchTerm]);

  const statusCounts = useMemo(() => {
    const c = { all: serviceHistory.length, pending: 0, accepted: 0, assigned: 0,
      "vehicle pick": 0, "in progress": 0, "servicing done": 0,
      "vehicle drop": 0, completed: 0, rejected: 0 };
    serviceHistory.forEach((s) => { if (c[s.status] !== undefined) c[s.status]++; });
    return c;
  }, [serviceHistory]);

  const canViewInvoice = (s) => s?.status === "completed";

  // ── loading ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-700 text-lg font-medium">Loading your service history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-2xl shadow-lg p-8 max-w-md border border-red-200">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchHistory}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 flex items-center gap-2 mx-auto"
          >
            <ArrowPathIcon className="h-5 w-5" /> Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── main render ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* ── Header ── */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
              <ClockIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Service History</h1>
              <p className="text-sm text-gray-500">Track all your bike service bookings</p>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Logged in as</p>
                  <p className="font-semibold text-gray-900">{currentUser?.name || "User"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <PhoneIcon className="w-4 h-4 text-blue-600" />
                <span>+91 {currentUser?.phone || currentUser?.phoneNumber || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Search + Filter ── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by vehicle number, service type, booking ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
            {["all","pending","accepted","assigned","vehicle pick","in progress","servicing done","vehicle drop","completed","rejected"].map((key) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  filter === key ? "bg-blue-600 text-white shadow-sm" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                <span className="capitalize">{key}</span>
                <span className={`ml-1.5 ${filter === key ? "text-blue-200" : "text-gray-400"}`}>
                  ({statusCounts[key] || 0})
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Cards ── */}
        {filteredHistory.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <WrenchScrewdriverIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Bookings Found</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? "Try adjusting your search term" : "You haven't made any bookings yet"}
            </p>
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700"
            >
              Book a Service
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredHistory.map((service) => {
              const statusCfg  = getStatusConfig(service.status);
              const stepIdx    = getCurrentStep(service.status);
              const isDoorstep = service.location !== "Service Center Visit";

              return (
                <div
                  key={service.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col"
                >
                  {/* Card Header */}
                  <div className="p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                          <WrenchScrewdriverIcon className="h-5 w-5 text-white" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-gray-900 truncate">{service.serviceType}</h3>
                          <p className="text-xs text-gray-400">{service.orderId}</p>
                        </div>
                      </div>

                      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border flex items-center gap-1 flex-shrink-0 ml-2 ${statusCfg.color}`}>
                        {statusCfg.icon}
                        <span className="capitalize">{service.status}</span>
                      </span>
                    </div>

                    {/* Vehicle info */}
                    <div className="bg-gray-50 rounded-lg p-2.5 space-y-1">
                      <p className="text-sm font-bold text-gray-900">{service.vehicleNumber}</p>
                      <p className="text-xs text-gray-500">
                        {service.vehicleBrand} {service.vehicleModel} • {service.fuelType}
                        {service.kilometer ? ` • ${service.kilometer} km` : ""}
                      </p>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-4 space-y-3 flex-1">
                    {/* Progress bar */}
                    {stepIdx >= 0 && (
                      <div>
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>Booking Placed</span>
                          <span>Completed</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(5, ((stepIdx + 1) / BOOKING_STEPS.length) * 100)}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Schedule */}
                    <div className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg">
                      <CalendarIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
                      <div className="text-xs">
                        <span className="text-gray-500">Scheduled: </span>
                        <span className="font-semibold text-gray-900">{service.date}</span>
                        {service.time !== "N/A" && (
                          <span className="text-gray-500"> • {service.time}</span>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    <div className="flex items-start gap-2">
                      <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className={`text-xs font-semibold mr-1 ${isDoorstep ? "text-purple-600" : "text-blue-600"}`}>
                          {isDoorstep ? "Doorstep" : "Center"}:
                        </span>
                        <span className="text-xs text-gray-600 line-clamp-2">{service.location}</span>
                      </div>
                    </div>

                    {/* Services count */}
                    {service.services.length > 0 && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <ListBulletIcon className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <span>{service.services.length} service{service.services.length > 1 ? "s" : ""} booked</span>
                      </div>
                    )}

                    {/* Store assigned */}
                  {/* ✅ NEW: Store/Center/Pending Logic */}
{service.status === "pending" ? (
  <div className="flex items-start gap-2 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
    <ClockIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
    <div className="text-xs">
      <p className="font-semibold text-yellow-800 mb-1">Waiting for store acceptance</p>
      <p className="text-yellow-700 text-sm">We'll notify you once accepted</p>
    </div>
  </div>
) : service.store ? (
  <div className="flex items-start gap-2 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
    <TruckIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
    <div className="text-xs min-w-0">
      <p className="font-semibold text-blue-800 truncate">{service.store.name}</p>
      {service.store.gst_number && (
        <p className="text-[10px] font-mono text-blue-600 font-bold mt-0.5">
          GST: {service.store.gst_number}
        </p>
      )}
      <p className="text-blue-600">{service.store.contact}</p>
    </div>
  </div>
) : service.centerLocation ? (
  <div className="flex items-start gap-2 p-2.5 bg-emerald-50 rounded-lg border border-emerald-100">
    <MapPinIcon className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
    <div className="text-xs">
      <p className="font-semibold text-emerald-800">Service Center</p>
      <p className="text-emerald-600 line-clamp-2">{service.centerLocation}</p>
    </div>
  </div>
) : null}
                    {/* Amount */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                      <div>
                        <p className="text-xs text-gray-500">Total Amount</p>
                        <p className="text-xs text-gray-400">{service.paymentMethod} • {service.paymentStatus}</p>
                      </div>
                      <div className="flex items-center text-xl font-bold text-gray-900">
                        <CurrencyRupeeIcon className="h-5 w-5" />
                        <span>{service.amount.toLocaleString("en-IN")}</span>
                      </div>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2">
                    <button
                      onClick={() => { setSelectedDetails(service); setShowDetailsModal(true); }}
                      className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-700 transition-colors"
                    >
                      View Details
                    </button>
                    {canViewInvoice(service) && (
                      <button
                        onClick={() => navigate(`/invoice/${service.bookingId}`)}
                        className="flex-1 bg-white border-2 border-blue-600 text-blue-600 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-50 flex items-center justify-center gap-1 transition-colors"
                      >
                        <DocumentTextIcon className="h-4 w-4" /> Invoice
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ════════════════════════════════
          Details Modal
      ════════════════════════════════ */}
      {showDetailsModal && selectedDetails && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowDetailsModal(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-lg font-bold">{selectedDetails.serviceType}</h3>
                <p className="text-sm text-blue-100">{selectedDetails.orderId} • {selectedDetails.createdAt}</p>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Status badge */}
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <span className={`px-3 py-1.5 rounded-full text-sm font-semibold border flex items-center gap-1.5 ${getStatusConfig(selectedDetails.status).color}`}>
                {getStatusConfig(selectedDetails.status).icon}
                <span className="capitalize">{selectedDetails.bookingStatus}</span>
              </span>
              {/* Mini progress */}
              <div className="flex items-center gap-1">
                {BOOKING_STEPS.map((step, idx) => {
                  const cur = getCurrentStep(selectedDetails.status);
                  return (
                    <div
                      key={step.key}
                      title={step.label}
                      className={`h-2 rounded-full transition-all ${
                        idx <= cur ? "w-5 bg-blue-600" : "w-2 bg-gray-200"
                      }`}
                    />
                  );
                })}
              </div>
            </div>

            {/* Scrollable body */}
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">

              {/* Vehicle + Schedule */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-1">Vehicle</p>
                  <p className="font-bold text-gray-900">{selectedDetails.vehicleNumber}</p>
                  <p className="text-sm text-gray-600">{selectedDetails.vehicleBrand} {selectedDetails.vehicleModel}</p>
                  <p className="text-xs text-gray-400">{selectedDetails.fuelType}{selectedDetails.kilometer ? ` • ${selectedDetails.kilometer} km` : ""}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                  <p className="text-xs text-gray-500 mb-1">Scheduled</p>
                  <p className="font-bold text-gray-900">{selectedDetails.date}</p>
                  <p className="text-sm text-gray-600">{selectedDetails.time}</p>
                  <p className="text-xs text-gray-400">Booked: {selectedDetails.createdAt}</p>
                </div>
              </div>

              {/* Location */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                  <MapPinIcon className="h-3.5 w-3.5" /> Service Location
                </p>
                <p className="text-sm font-semibold text-gray-900">{selectedDetails.location}</p>
              </div>

              {/* Issue */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Issue / Comments</p>
                <p className="text-sm text-gray-800">{selectedDetails.issueDescription}</p>
              </div>

              {/* Services List */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
                    <ListBulletIcon className="h-4 w-4 text-blue-600" />
                    Services Booked ({selectedDetails.services?.length || 0})
                  </p>
                  {selectedDetails.services?.length > 3 && (
                    <button
                      onClick={() => { setSelectedService(selectedDetails); setShowServicesModal(true); }}
                      className="text-xs text-blue-600 font-semibold hover:underline"
                    >
                      View All →
                    </button>
                  )}
                </div>

                {selectedDetails.services?.length ? (
                  <div className="space-y-2">
                    {selectedDetails.services.slice(0, 3).map((svc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-2.5">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{svc.serviceName}</p>
                          <p className="text-xs text-gray-400">Qty: {svc.quantity}</p>
                        </div>
                        <p className="text-sm font-bold text-blue-600 flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4" />{svc.servicePrice}
                        </p>
                      </div>
                    ))}
                    {selectedDetails.services.length > 3 && (
                      <p className="text-xs text-center text-gray-400 pt-1">
                        +{selectedDetails.services.length - 3} more services
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-3">No services listed</p>
                )}
              </div>

              {/* Assigned Store */}
          {/* ✅ NEW: Store/Center/Pending in Modal */}
{selectedDetails.status === "pending" ? (
  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
    <p className="text-sm text-yellow-700 font-bold mb-3 flex items-center gap-2">
      <ClockIcon className="h-5 w-5" /> Status Update
    </p>
    <div className="space-y-1">
      <p className="text-lg font-bold text-yellow-900">Waiting for Store</p>
      <p className="text-sm text-yellow-800">We'll notify you via SMS when a store accepts your booking</p>
    </div>
  </div>
) : selectedDetails.store ? (
  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
    <p className="text-sm text-blue-700 font-bold mb-3 flex items-center gap-2">
      <TruckIcon className="h-5 w-5" /> Assigned Store
    </p>
    <div className="space-y-2">
      <p className="text-lg font-bold text-gray-900">{selectedDetails.store.name}</p>
      {selectedDetails.store.gst_number && (
        <div className="flex items-center gap-2 bg-white/50 px-3 py-2 rounded-lg border border-blue-200">
          <span className="text-xs font-bold text-blue-600">GST:</span>
          <span className="text-sm font-mono font-bold text-gray-900">
            {selectedDetails.store.gst_number}
          </span>
        </div>
      )}
      <p className="text-sm text-gray-700">{selectedDetails.store.address}</p>
      <a
        href={`tel:${selectedDetails.store.contact}`}
        className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
      >
        <PhoneIcon className="h-4 w-4" /> {selectedDetails.store.contact}
      </a>
    </div>
  </div>
) : selectedDetails.centerLocation ? (
  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
    <p className="text-sm text-emerald-700 font-bold mb-3 flex items-center gap-2">
      <MapPinIcon className="h-5 w-5" /> Service Center
    </p>
    <p className="text-base font-semibold text-gray-900 line-clamp-2">
      {selectedDetails.centerLocation}
    </p>
  </div>
) : null}

              {/* Payment */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedDetails.paymentMethod}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  selectedDetails.paymentStatus === "Completed"
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}>
                  {selectedDetails.paymentStatus}
                </span>
              </div>

              {/* Total */}
              <div className="flex items-center justify-between p-4 bg-blue-600 rounded-xl text-white">
                <span className="font-bold text-lg">Total Amount</span>
                <span className="text-2xl font-bold flex items-center">
                  <CurrencyRupeeIcon className="h-6 w-6" />
                  {selectedDetails.amount.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
              {canViewInvoice(selectedDetails) && (
                <button
                  onClick={() => navigate(`/invoice/${selectedDetails.bookingId}`)}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors"
                >
                  <DocumentTextIcon className="h-5 w-5" /> View Invoice
                </button>
              )}
              <button
                onClick={() => setShowDetailsModal(false)}
                className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════
          All Services Modal
      ════════════════════════════════ */}
      {showServicesModal && selectedService && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowServicesModal(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ListBulletIcon className="h-6 w-6" />
                <div>
                  <h3 className="text-lg font-bold">All Services</h3>
                  <p className="text-sm text-blue-100">{selectedService.orderId}</p>
                </div>
              </div>
              <button onClick={() => setShowServicesModal(false)} className="hover:bg-white/20 rounded-lg p-2 transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
              {selectedService.services?.map((svc, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">{svc.serviceName}</p>
                    <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border mt-1 inline-block">
                      Qty: {svc.quantity}
                    </span>
                    <p className="text-sm font-bold text-blue-600 mt-2 flex items-center">
                      <CurrencyRupeeIcon className="h-4 w-4" />{svc.servicePrice}
                    </p>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between p-4 bg-blue-600 rounded-xl text-white mt-2">
                <span className="font-bold">Total</span>
                <span className="text-xl font-bold flex items-center">
                  <CurrencyRupeeIcon className="h-5 w-5" />
                  {selectedService.amount.toLocaleString("en-IN")}
                </span>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setShowServicesModal(false)}
                className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceHistory;